// Upgrade screen — the Free -> Pro conversion page.
//
// Visual primary: Stripe / Linear pricing pages — restrained, confident,
// one elevated hero card. Wired to POST /v1/billing/checkout
// (workers/api-gateway/src/handlers/billing.ts); the mock path upgrades
// the account instantly and redirects to #account?upgraded=1.

import { useState } from 'preact/hooks';
import type { JSX } from 'preact';
import { useAuth } from '@vibeshell/hooks';
import { Check, Sparkles, ArrowRight } from 'lucide-preact';
import { PomelaCheckoutModal } from '../components/PomelaCheckout';
import { TopBar } from './Settings';
import { t } from '../lib/i18n';

// Value-based pricing copy — deliberately NO per-day / per-month build
// counts. The real economy is an opaque credit pool (the credit panel
// shows only a balance), so this marketing surface describes what you GET,
// never a number that would expose the build/interaction conversion.
// Fair-use limits still apply and are disclosed in plain language below.
// i18n keys (resolved via t() at render so the active locale wins).
const FREE_FEATURES = ['upg.free1', 'upg.free2', 'upg.free3', 'upg.free4'];
const PRO_FEATURES = ['upg.pro1', 'upg.pro2', 'upg.pro3', 'upg.pro4', 'upg.pro5'];

export function Upgrade(props: { onClose: () => void; onSignIn: () => void }): JSX.Element {
  const { session, loading: authLoading } = useAuth();
  const busy = false; // checkout state lives inside PomelaCheckout now
  const [error, setError] = useState('');
  // Hold the "Already Pro" affordance behind the auth check — otherwise a
  // Pro user briefly sees the Upgrade CTA before the screen flips.
  // (NIGHT_AUDIT §3.1.)
  const isPro = !authLoading && session.plan === 'pro';

  // §LG2 — Go Pro opens the EMBEDDED Pomela Checkout (PayPal-processed; the
  // subscription is created server-side and success is server-verified). If the
  // PayPal SDK can't load, the checkout itself offers the honest redirect fallback.
  const [showCheckout, setShowCheckout] = useState(false);
  function goPro(): void {
    if (!session.signedIn) {
      props.onSignIn();
      return;
    }
    setError('');
    setShowCheckout(true);
  }

  return (
    <div style={{ minHeight: '100dvh', background: 'var(--bg)' }}>
      <TopBar title={t('sb.upgrade.title')} eyebrow={t('sb.upgrade.eyebrow')} icon={Sparkles} onClose={props.onClose} />

      <div class="signin-step" style={{ maxWidth: 440, width: '100%', margin: '0 auto', padding: '20px 20px 56px' }}>
        {/* hero */}
        <div style={{ textAlign: 'center', marginBottom: 26 }}>
          <div style={{ ...proBadge, margin: '0 auto 14px' }}>
            <Sparkles size={13} /> {t('upg.proBadge')}
          </div>
          <h1 style={{ fontSize: 30, fontWeight: 700, letterSpacing: '-0.03em', lineHeight: 1.12, margin: '0 0 8px' }}>
            {t('upg.heroTitle')}
          </h1>
          <p style={{ fontSize: 15, lineHeight: 1.5, color: 'var(--fg-muted)', margin: 0 }}>
            {t('upg.heroSub')}
          </p>
        </div>

        {/* Pro card — the hero */}
        <div style={proCard}>
          <div style={proCardSheen} aria-hidden="true" />
          <div style={{ position: 'relative' }}>
            <div style={{ display: 'flex', alignItems: 'baseline', justifyContent: 'space-between' }}>
              <span style={{ fontSize: 15, fontWeight: 700, color: '#fff' }}>Pro</span>
              <span style={{ fontSize: 12, fontWeight: 700, letterSpacing: '0.04em', color: 'rgba(255,255,255,0.78)' }}>
                {t('upg.recommended')}
              </span>
            </div>
            <div style={{ display: 'flex', alignItems: 'baseline', gap: 5, margin: '8px 0 16px' }}>
              <span style={{ fontSize: 42, fontWeight: 700, letterSpacing: '-0.04em', color: '#fff' }}>$19</span>
              <span style={{ fontSize: 14, color: 'rgba(255,255,255,0.72)' }}>{t('upg.perMonth')}</span>
            </div>
            <ul style={featureList}>
              {PRO_FEATURES.map((f) => (
                <li key={f} style={featureRow}>
                  <span style={proCheck}>
                    <Check size={12} strokeWidth={3.5} />
                  </span>
                  <span style={{ color: 'rgba(255,255,255,0.95)' }}>{t(f)}</span>
                </li>
              ))}
            </ul>
            {isPro ? (
              <div style={{ ...ctaBtn, background: 'rgba(255,255,255,0.18)', color: '#fff', cursor: 'default' }}>
                <Check size={16} strokeWidth={3} /> {t('upg.onPro')}
              </div>
            ) : (
              <button
                type="button"
                class="plan-glassbtn"
                onClick={goPro}
                disabled={busy}
                style={{ ...ctaBtn, background: '#fff', color: '#1A1320', opacity: busy ? 0.7 : 1 }}
              >
                {busy ? t('upg.starting') : session.signedIn ? t('upg.goPro') : t('upg.signInPro')}
                {!busy && <ArrowRight size={16} />}
              </button>
            )}
            {error && (
              <p style={{ fontSize: 12.5, color: '#FFE0E0', margin: '10px 0 0', textAlign: 'center' }}>
                {error}
              </p>
            )}
          </div>
        </div>

        {/* Free card */}
        <div style={freeCard}>
          <div style={{ display: 'flex', alignItems: 'baseline', justifyContent: 'space-between' }}>
            <span style={{ fontSize: 15, fontWeight: 700 }}>{t('upg.free')}</span>
            {!isPro && (
              <span style={{ fontSize: 12, fontWeight: 600, color: 'var(--fg-subtle)' }}>{t('upg.yourPlan')}</span>
            )}
          </div>
          <div style={{ display: 'flex', alignItems: 'baseline', gap: 5, margin: '8px 0 16px' }}>
            <span style={{ fontSize: 34, fontWeight: 700, letterSpacing: '-0.03em' }}>$0</span>
            <span style={{ fontSize: 14, color: 'var(--fg-muted)' }}>{t('upg.forever')}</span>
          </div>
          <ul style={featureList}>
            {FREE_FEATURES.map((f) => (
              <li key={f} style={featureRow}>
                <span style={freeCheck}>
                  <Check size={12} strokeWidth={3.5} />
                </span>
                <span style={{ color: 'var(--fg-muted)' }}>{t(f)}</span>
              </li>
            ))}
          </ul>
        </div>

        {/* Refund + cancellation disclosure — premium, transparent.
            Premium products earn trust by saying the quiet part out loud. */}
        <section className="upgrade-refund">
          <h3 className="upgrade-refund__title">{t('upg.cancelTitle')}</h3>
          <ul className="upgrade-refund__list">
            <li>{t('upg.refund1')}</li>
            <li>{t('upg.refund2')}</li>
            <li>{t('upg.refund3')}</li>
          </ul>
          <p className="upgrade-refund__foot">
            {t('upg.refundFoot')}
          </p>
        </section>
      </div>

      <PomelaCheckoutModal
        mode="pro"
        open={showCheckout}
        onClose={() => setShowCheckout(false)}
        onDone={(d) => {
          if (d.kind === 'pro-active' || d.kind === 'mock-upgraded') {
            setTimeout(() => { window.location.reload(); }, 1200);
          }
        }}
      />
    </div>
  );
}

// ─── styles ─────────────────────────────────────────────────────────────────

const proBadge: JSX.CSSProperties = {
  display: 'inline-flex',
  alignItems: 'center',
  gap: 6,
  padding: '5px 12px',
  borderRadius: 9999,
  background: 'var(--gradient-brand)',
  color: '#fff',
  fontSize: 12,
  fontWeight: 700,
  letterSpacing: '0.02em'
};

const proCard: JSX.CSSProperties = {
  position: 'relative',
  overflow: 'hidden',
  background: 'var(--gradient-brand)',
  borderRadius: 20,
  padding: 24,
  boxShadow: '0 22px 50px -18px rgba(124,77,255,0.55)',
  marginBottom: 16
};

const proCardSheen: JSX.CSSProperties = {
  position: 'absolute',
  inset: 0,
  background: 'radial-gradient(120% 80% at 25% 0%, rgba(255,255,255,0.34), transparent 62%)',
  pointerEvents: 'none'
};

const freeCard: JSX.CSSProperties = {
  background: 'var(--bg-elevated)',
  border: '1px solid var(--border)',
  borderRadius: 18,
  padding: 22,
  boxShadow: 'var(--shadow-sm)'
};

const featureList: JSX.CSSProperties = {
  listStyle: 'none',
  padding: 0,
  margin: '0 0 18px',
  display: 'flex',
  flexDirection: 'column',
  gap: 9
};

const featureRow: JSX.CSSProperties = {
  display: 'flex',
  alignItems: 'flex-start',
  gap: 9,
  fontSize: 14,
  lineHeight: 1.4
};

const proCheck: JSX.CSSProperties = {
  flexShrink: 0,
  width: 18,
  height: 18,
  borderRadius: 9999,
  background: 'rgba(255,255,255,0.24)',
  color: '#fff',
  display: 'grid',
  placeItems: 'center',
  marginTop: 1
};

const freeCheck: JSX.CSSProperties = {
  flexShrink: 0,
  width: 18,
  height: 18,
  borderRadius: 9999,
  background: 'var(--bg-subtle)',
  color: 'var(--fg-muted)',
  display: 'grid',
  placeItems: 'center',
  marginTop: 1
};

const ctaBtn: JSX.CSSProperties = {
  width: '100%',
  padding: '13px 18px',
  borderRadius: 12,
  border: 'none',
  fontSize: 15,
  fontWeight: 700,
  cursor: 'pointer',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  gap: 8
};
